import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, TOKEN_KEY } from "@/lib/api";
import { toast } from "sonner";

const HERO_IMG =
  "https://images.unsplash.com/photo-1441986300917-64674bd600d8?w=2000&q=85&auto=format&fit=crop";

const BRAND_LOGO =
  "https://customer-assets.emergentagent.com/job_mativex-hub/artifacts/o676nr9o_92db1180-0be8-412d-b0d1-823df7098172.png";

function LiveTicker({ inverse = false }) {
  const [data, setData] = useState(null);
  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      try {
        const auth = await api.post("/auth/login", { username: "admin", password: "admin123" });
        const t = auth.data.token;
        const [k, p] = await Promise.all([
          fetch(`${api.defaults.baseURL}/v2/kpis?period=MTD`, { headers: { Authorization: `Bearer ${t}` } }).then((r) => r.json()),
          fetch(`${api.defaults.baseURL}/pulse/feed?limit=1`, { headers: { Authorization: `Bearer ${t}` } }).then((r) => r.json()),
        ]);
        if (cancelled) return;
        setData({ kpi: k, last: p.transactions?.[0] });
      } catch {/* */}
    };
    load();
    const id = setInterval(load, 30000);
    return () => { cancelled = true; clearInterval(id); };
  }, []);
  const fmt = (v) => {
    if (v == null) return "—";
    const a = Math.abs(v);
    if (a >= 1e7) return `₹${(v / 1e7).toFixed(2)} Cr`;
    if (a >= 1e5) return `₹${(v / 1e5).toFixed(2)} L`;
    return `₹${Math.round(v).toLocaleString("en-IN")}`;
  };
  const items = [
    ["MTD", fmt(data?.kpi?.net_sales)],
    ["TXNS", data?.kpi?.transactions?.toLocaleString("en-IN") ?? "—"],
    ["ATV", fmt(data?.kpi?.atv)],
    ["LAST VOUCHER", data?.last ? fmt(data.last.amount) : "—"],
  ];
  return (
    <div className="flex items-center gap-4 flex-wrap text-white">
      <span className="flex items-center gap-2 text-[10px] uppercase tracking-[0.3em] text-[#7FB6FF] shrink-0">
        <span className="inline-block w-1.5 h-1.5 bg-[#7FB6FF] animate-pulse" />Live
      </span>
      {items.map(([k, v]) => (
        <span key={k} className="flex items-baseline gap-1.5 shrink-0">
          <span className="text-[9px] md:text-[10px] uppercase tracking-[0.22em] text-white/45">{k}</span>
          <span className="font-mono text-xs md:text-sm">{v}</span>
        </span>
      ))}
    </div>
  );
}

export default function Login() {
  const nav = useNavigate();
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e, demo = false) => {
    e?.preventDefault?.();
    setLoading(true);
    try {
      const u = demo ? "admin" : username;
      const p = demo ? "admin123" : password;
      const { data } = await api.post("/auth/login", { username: u, password: p });
      localStorage.setItem(TOKEN_KEY, data.token);
      toast.success("Welcome to Mativex");
      nav("/daily");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Login failed");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="relative min-h-screen w-full overflow-hidden bg-[#040814] text-white">
      {/* ====== Full-bleed photographic background ====== */}
      <img
        src={HERO_IMG}
        alt=""
        className="absolute inset-0 w-full h-full object-cover"
        style={{ objectPosition: "center 60%" }}
      />
      {/* Heavy editorial overlay so text stays readable */}
      <div className="absolute inset-0 bg-gradient-to-r from-[#040814]/95 via-[#040814]/75 to-[#040814]/85" />
      <div className="absolute inset-0 bg-gradient-to-t from-[#040814] via-[#040814]/40 to-[#040814]/70" />
      {/* Extra darken on right half for the form card */}
      <div className="absolute inset-y-0 right-0 w-full md:w-1/2 bg-gradient-to-l from-[#040814]/85 to-transparent" />
      <div
        className="absolute inset-0 opacity-[0.18] mix-blend-overlay pointer-events-none"
        style={{ backgroundImage: "radial-gradient(rgba(255,255,255,0.16) 1px, transparent 1px)", backgroundSize: "3px 3px" }}
      />

      {/* ====== Content ====== */}
      <div className="relative z-10 min-h-screen flex flex-col">
        {/* Main row (header removed — session moved to footer ticker) */}
        <main className="flex-1 flex flex-col md:flex-row items-center justify-center px-5 md:px-10 lg:px-16 py-8 md:py-10 gap-10 md:gap-16">
          {/* Left — logo + headline */}
          <div className="flex-1 max-w-2xl flex flex-col items-center text-center">
            <img
              src={BRAND_LOGO}
              alt="Mativex"
              data-testid="brand-mark"
              className="h-16 md:h-24 lg:h-28 w-auto select-none mb-5 md:mb-6 drop-shadow-[0_0_50px_rgba(30,64,255,0.22)] fade-in-up"
              style={{ filter: "invert(1) hue-rotate(180deg) saturate(1.2)", animationDelay: "0ms" }}
              draggable={false}
            />
            <div className="flex items-center gap-3 mb-5 fade-in-up" style={{ animationDelay: "120ms" }}>
              <span className="h-px w-8 md:w-10 bg-[#7FB6FF]" />
              <span className="text-[10px] uppercase tracking-[0.3em] text-[#7FB6FF]">
                For operators who refuse to guess
              </span>
              <span className="h-px w-8 md:w-10 bg-[#7FB6FF]" />
            </div>
            <h1 className="font-display text-[34px] md:text-[42px] lg:text-[54px] leading-[1.05] tracking-[-0.02em] fade-in-up" style={{ animationDelay: "240ms" }}>
              What's selling. <span className="italic font-light text-white/85">What's stuck.</span> What's next.
            </h1>
            <p className="mt-5 max-w-md text-[14px] leading-relaxed text-white/75">
              A morning intelligence brief for retail. Inventory, sales, vendors and people — one terminal, read before the doors open.
            </p>

            {/* Stats row */}
            <div className="mt-7 grid grid-cols-3 gap-4 md:gap-6 max-w-md w-full">
              {[["8,200", "SKUs"], ["180 days", "of trade"], ["25", "Vendors"]].map(([n, l]) => (
                <div key={l} className="border-t border-white/15 pt-2.5">
                  <div className="font-mono text-base md:text-xl">{n}</div>
                  <div className="text-[9px] md:text-[10px] uppercase tracking-[0.22em] mt-1 text-white/55">{l}</div>
                </div>
              ))}
            </div>
          </div>

          {/* Right — floating form card */}
          <div className="w-full md:w-[420px] shrink-0 fade-in-up" style={{ animationDelay: "360ms" }}>
            <div
              data-testid="login-card"
              className="relative bg-[#0a0f1e]/85 backdrop-blur-xl border border-white/12 shadow-[0_30px_80px_-20px_rgba(0,0,0,0.6)] p-6 md:p-8"
            >
              {/* corner ticks */}
              {["top-0 left-0 border-t border-l", "top-0 right-0 border-t border-r",
                "bottom-0 left-0 border-b border-l", "bottom-0 right-0 border-b border-r"].map((c) => (
                <span key={c} className={`absolute ${c} w-3 h-3 border-[#1E40FF]`} />
              ))}

              <div className="text-[10px] uppercase tracking-[0.3em] text-[#7FB6FF] mb-2">Access</div>
              <h2 className="font-display text-2xl md:text-3xl tracking-tight leading-tight text-white">
                Sign in to your terminal.
              </h2>
              <p className="text-[13px] text-white/65 mt-2.5 leading-relaxed">
                One tap loads a populated demo with 180 days of trade. Or sign in with
                {" "}<span className="font-mono text-white">admin / admin123</span>.
              </p>

              <button
                type="button"
                data-testid="demo-login-button"
                onClick={(e) => submit(e, true)}
                disabled={loading}
                className="mt-6 w-full h-12 bg-[#1E40FF] hover:bg-[#1735d8] text-white font-mono text-[11px] uppercase tracking-[0.28em] transition-colors disabled:opacity-60 inline-flex items-center justify-center gap-3"
              >
                {loading ? "Signing in…" : (<><span>Enter the terminal</span><span>→</span></>)}
              </button>

              <div className="flex items-center gap-3 my-6">
                <div className="flex-1 border-t border-white/12" />
                <span className="text-[9px] uppercase tracking-[0.3em] text-white/45">or sign in manually</span>
                <div className="flex-1 border-t border-white/12" />
              </div>

              <form onSubmit={submit} className="space-y-3">
                <div>
                  <div className="text-[9px] uppercase tracking-[0.3em] text-white/55 mb-1.5">Username</div>
                  <input
                    data-testid="login-username"
                    value={username}
                    onChange={(e) => setUsername(e.target.value)}
                    className="w-full bg-transparent border border-white/15 hover:border-white/25 focus:border-[#1E40FF] px-3 h-11 font-mono text-sm text-white placeholder-white/30 focus:outline-none transition-colors"
                    placeholder="admin"
                  />
                </div>
                <div>
                  <div className="text-[9px] uppercase tracking-[0.3em] text-white/55 mb-1.5">Password</div>
                  <input
                    data-testid="login-password"
                    type="password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    className="w-full bg-transparent border border-white/15 hover:border-white/25 focus:border-[#1E40FF] px-3 h-11 font-mono text-sm text-white placeholder-white/30 focus:outline-none transition-colors"
                    placeholder="admin123"
                  />
                </div>
                <button
                  type="submit"
                  data-testid="login-submit"
                  disabled={loading}
                  className="w-full h-11 border border-white/20 hover:bg-white/5 transition-colors font-mono text-[11px] uppercase tracking-[0.22em] text-white disabled:opacity-50"
                >
                  {loading ? "…" : "Sign in"}
                </button>
              </form>

              <p className="text-[10px] text-white/50 mt-6 leading-relaxed text-center">
                Built for CEOs, category heads, procurement, store ops and HR.
              </p>
            </div>
          </div>
        </main>

        {/* Footer — live ticker + session stamp */}
        <footer className="relative z-10 border-t border-white/10 px-5 md:px-10 py-3 md:py-4 flex items-center justify-between gap-4 md:gap-6 flex-wrap">
          <LiveTicker />
          <div className="font-mono text-[10px] md:text-[11px] text-white/55 shrink-0 flex items-center gap-3">
            <span>{new Date().toLocaleString("en-IN", { weekday: "short", day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" })}</span>
            <span className="text-white/20">·</span>
            <span className="uppercase tracking-[0.22em]">v1.0 · FY April–March</span>
          </div>
        </footer>
      </div>
    </div>
  );
}
